import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Table, Modal, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('superAdminToken'), []);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [errorProfile, setErrorProfile] = useState('');

  // Create restaurant form
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    restaurantName: '',
    restaurantDescription: ''
  });
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState('');
  const [createError, setCreateError] = useState('');

  // Restaurants list
  const [restaurants, setRestaurants] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');
  const [listSuccess, setListSuccess] = useState('');

  // Inline edits map
  const [editRows, setEditRows] = useState({});
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | disabled

  // Delete restaurant modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Subdomain management state
  const [subdomainModal, setSubdomainModal] = useState({ show: false, restaurant: null });
  const [subdomain, setSubdomain] = useState('');
  const [subdomainSaving, setSubdomainSaving] = useState(false);
  const [subdomainMessage, setSubdomainMessage] = useState('');
  const [subdomainError, setSubdomainError] = useState('');

  // Subdomain management functions
  const handleSubdomainClick = async (restaurant) => {
    setSubdomainModal({ show: true, restaurant });
    setSubdomain(restaurant.subdomain || '');
    setSubdomainMessage('');
    setSubdomainError('');
  };

  const handleGenerateSubdomain = async () => {
    if (!subdomainModal.restaurant) return;
    
    setSubdomainSaving(true);
    setSubdomainError('');
    setSubdomainMessage('');

    try {
      const response = await axios.post('/api/superadmin/generate-subdomain', {
        restaurantId: subdomainModal.restaurant._id
      }, {
        headers: { 'x-auth-token': token }
      });
      const data = response.data;
      
      setSubdomain(data.subdomain);
      setSubdomainMessage('Субдомен автоматически сгенерирован на основе названия ресторана!');
    } catch (error) {
      if (error.response?.data?.message) {
        setSubdomainError(error.response.data.message);
      } else {
        setSubdomainError('Ошибка генерации субдомена');
      }
    } finally {
      setSubdomainSaving(false);
    }
  };

  const handleSubdomainSave = async () => {
    if (!subdomainModal.restaurant || !subdomain.trim()) {
      setSubdomainError('Введите субдомен');
      return;
    }

    setSubdomainSaving(true);
    setSubdomainError('');
    setSubdomainMessage('');

    try {
      // Сохраняем субдомен
      await axios.put('/api/superadmin/subdomain', {
        restaurantId: subdomainModal.restaurant._id,
        subdomain: subdomain.trim().toLowerCase()
      }, {
        headers: { 'x-auth-token': token }
      });

      setSubdomainMessage('Субдомен успешно сохранен!');
      
      // Обновляем список ресторанов
      await loadRestaurants();
    } catch (error) {
      if (error.response?.data?.message) {
        setSubdomainError(error.response.data.message);
      } else {
        setSubdomainError('Ошибка сохранения субдомена');
      }
    } finally {
      setSubdomainSaving(false);
    }
  };

  const handleSubdomainDelete = async () => {
    if (!subdomainModal.restaurant) return;
    
    if (!window.confirm(`Вы уверены, что хотите удалить субдомен "${subdomainModal.restaurant.subdomain}" для ресторана "${subdomainModal.restaurant.restaurantName}"?`)) {
      return;
    }

    setSubdomainSaving(true);
    setSubdomainError('');
    setSubdomainMessage('');

    try {
      await axios.delete('/api/superadmin/subdomain', {
        data: {
          restaurantId: subdomainModal.restaurant._id
        },
        headers: { 'x-auth-token': token }
      });

      setSubdomainMessage('Субдомен успешно удален!');
      setSubdomain('');
      
      // Обновляем список ресторанов
      await loadRestaurants();
    } catch (error) {
      if (error.response?.data?.message) {
        setSubdomainError(error.response.data.message);
      } else {
        setSubdomainError('Ошибка удаления субдомена');
      }
    } finally {
      setSubdomainSaving(false);
    }
  };

  const handleSubdomainModalClose = () => {
    setSubdomainModal({ show: false, restaurant: null });
    setSubdomain('');
    setSubdomainMessage('');
    setSubdomainError('');
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) {
          navigate('/superadmin/login');
          return;
        }
        setLoadingProfile(true);
        setErrorProfile('');
        const res = await axios.get('/api/superadmin/profile', {
          headers: { 'x-auth-token': token }
        });
        setProfile(res.data);
      } catch (err) {
        setErrorProfile(err.response?.data?.message || 'Не удалось загрузить профиль суперадмина');
      } finally {
        setLoadingProfile(false);
      }
    };
    load();
  }, [token, navigate]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const createRestaurant = async (e) => {
    e.preventDefault();
    try {
      if (!token) {
        setCreateError('Сессия истекла. Войдите снова.');
        return;
      }
      setCreating(true);
      setCreateError('');
      setCreateSuccess('');
      const payload = { ...form };
      const res = await axios.post('/api/superadmin/restaurants', payload, {
        headers: { 'x-auth-token': token }
      });
      setCreateSuccess(`Ресторан создан: ${res.data.restaurantName}`);
      setForm({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        restaurantName: '',
        restaurantDescription: ''
      });
      await loadRestaurants();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Не удалось создать ресторан';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  const loadRestaurants = async () => {
    try {
      if (!token) return;
      setLoadingList(true);
      setListError('');
      setListSuccess('');
      const res = await axios.get('/api/superadmin/restaurants', {
        headers: { 'x-auth-token': token }
      });
      setRestaurants(res.data || []);
    } catch (err) {
      setListError(err.response?.data?.message || 'Не удалось загрузить рестораны');
    } finally {
      setLoadingList(false);
    }
  };

  const beginEdit = (id) => {
    const row = restaurants.find(r => r._id === id);
    if (!row) return;
    setEditRows(prev => ({
      ...prev,
      [id]: {
        restaurantName: row.restaurantName || '',
        restaurantDescription: row.restaurantDescription || '',
        email: row.email || '',
        phone: row.phone || '',
        isActive: typeof row.isActive === 'boolean' ? row.isActive : true
      }
    }));
    setListError('');
    setListSuccess('');
  };

  const cancelEdit = (id) => {
    setEditRows(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const onEditChange = (id, field, value) => {
    setEditRows(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const saveEdit = async (id) => {
    try {
      if (!token) {
        setListError('Сессия истекла. Войдите снова.');
        return;
      }
      const payload = editRows[id];
      if (!payload) return;
      setListError('');
      setListSuccess('');
      await axios.put(`/api/superadmin/restaurants/${id}`, payload, {
        headers: { 'x-auth-token': token }
      });
      setListSuccess('Изменения сохранены');
      await loadRestaurants();
      cancelEdit(id);
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось сохранить изменения';
      setListError(msg);
    }
  };

  const deleteRestaurant = async (id) => {
    try {
      if (!token) {
        setListError('Сессия истекла. Войдите снова.');
        return;
      }
      setListError('');
      setListSuccess('');
      await axios.delete(`/api/superadmin/restaurants/${id}`, {
        headers: { 'x-auth-token': token }
      });
      setListSuccess('Ресторан удалён');
      await loadRestaurants();
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось удалить ресторан';
      setListError(msg);
    }
  };

  // Delete restaurant functions
  const handleDeleteClick = (restaurant) => {
    setRestaurantToDelete(restaurant);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!restaurantToDelete) return;
    
    try {
      setDeleting(true);
      setListError('');
      setListSuccess('');
      
      const token = localStorage.getItem('superAdminToken');
      if (!token) {
        setListError('Сессия истекла. Войдите снова.');
        setDeleting(false);
        return;
      }

      await axios.delete(`/api/superadmin/restaurants/${restaurantToDelete._id}`, {
        headers: { 'x-auth-token': token }
      });

      setListSuccess(`Ресторан ${restaurantToDelete.restaurantName} успешно удален`);
      
      // Update local state
      setRestaurants((prev) => prev.filter((r) => r._id !== restaurantToDelete._id));
      
      // Close modal
      setShowDeleteModal(false);
      setRestaurantToDelete(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось удалить ресторан';
      setListError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRestaurantToDelete(null);
  };

  // Quick toggle active without entering edit mode
  const quickToggleActive = async (id, nextActive) => {
    try {
      if (!token) {
        setListError('Сессия истекла. Войдите снова.');
        return;
      }
      setListError('');
      setListSuccess('');
      await axios.put(`/api/superadmin/restaurants/${id}`, { isActive: !!nextActive }, {
        headers: { 'x-auth-token': token }
      });
      await loadRestaurants();
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось изменить статус';
      setListError(msg);
    }
  };

  useEffect(() => {
    loadRestaurants();
  }, [token]);

  if (loadingProfile) {
    return <div className="mt-5 text-center">Загрузка профиля суперадмина...</div>;
  }

  if (errorProfile) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{errorProfile}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="mb-4">
        <Col>
          <h1>Панель суперадмина</h1>
          <p className="mb-0">Вы вошли как: <strong>{profile?.email}</strong></p>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title>Добавить ресторан</Card.Title>
              {createSuccess && <Alert variant="success">{createSuccess}</Alert>}
              {createError && <Alert variant="danger">{createError}</Alert>}
              <Form onSubmit={createRestaurant}>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Имя владельца</Form.Label>
                      <Form.Control name="name" value={form.name} onChange={onChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email владельца</Form.Label>
                      <Form.Control type="email" name="email" value={form.email} onChange={onChange} required />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Пароль владельца</Form.Label>
                      <Form.Control type="password" name="password" value={form.password} onChange={onChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Телефон</Form.Label>
                      <Form.Control name="phone" value={form.phone} onChange={onChange} />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Адрес</Form.Label>
                  <Form.Control name="address" value={form.address} onChange={onChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Название ресторана</Form.Label>
                  <Form.Control name="restaurantName" value={form.restaurantName} onChange={onChange} required />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Описание ресторана</Form.Label>
                  <Form.Control as="textarea" rows={2} name="restaurantDescription" value={form.restaurantDescription} onChange={onChange} />
                </Form.Group>

                <Button type="submit" variant="primary" disabled={creating}>
                  {creating ? 'Создание...' : 'Создать ресторан'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title className="d-flex align-items-center gap-2">
                <span>Рестораны</span>
                <span className="text-muted small">({restaurants.length})</span>
              </Card.Title>
              <Row className="g-2 mb-2">
                <Col>
                  <Form.Control
                    size="sm"
                    placeholder="Поиск по названию или email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Select
                    size="sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Все</option>
                    <option value="active">Активные</option>
                    <option value="disabled">Отключенные</option>
                  </Form.Select>
                </Col>
              </Row>
              {listError && <Alert variant="danger">{listError}</Alert>}
              {loadingList ? (
                <div className="text-muted">Загрузка...</div>
              ) : restaurants.length === 0 ? (
                <div className="text-muted">Пока нет ресторанов</div>
              ) : (
                <Table bordered hover responsive size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Email</th>
                      <th>Телефон</th>
                      <th>Субдомен</th>
                      <th>Статус</th>
                      <th>Создан</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(restaurants
                      .filter((r) => {
                        const q = searchQuery.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          String(r.restaurantName || '').toLowerCase().includes(q) ||
                          String(r.email || '').toLowerCase().includes(q)
                        );
                      })
                      .filter((r) => {
                        if (statusFilter === 'all') return true;
                        if (statusFilter === 'active') return !!r.isActive;
                        return !r.isActive;
                      })
                    ).map((r) => {
                      const isEditing = !!editRows[r._id];
                      const edit = editRows[r._id] || {};
                      return (
                        <tr key={r._id}>
                          <td style={{ minWidth: 160 }}>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={edit.restaurantName}
                                onChange={(e) => onEditChange(r._id, 'restaurantName', e.target.value)}
                              />
                            ) : (
                              r.restaurantName
                            )}
                          </td>
                          <td style={{ minWidth: 200 }}>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                type="email"
                                value={edit.email}
                                onChange={(e) => onEditChange(r._id, 'email', e.target.value)}
                              />
                            ) : (
                              r.email
                            )}
                          </td>
                          <td style={{ minWidth: 140 }}>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={edit.phone}
                                onChange={(e) => onEditChange(r._id, 'phone', e.target.value)}
                              />
                            ) : (
                              r.phone || '-'
                            )}
                          </td>
                          <td style={{ minWidth: 120 }}>
                            <div className="d-flex align-items-center gap-1">
                              {r.subdomain ? (
                                <span className="badge bg-primary">
                                  {r.subdomain}
                                </span>
                              ) : (
                                <span className="text-muted small">Нет</span>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline-secondary"
                                onClick={() => handleSubdomainClick(r)}
                                title="Управление субдоменом"
                              >
                                ⚙️
                              </Button>
                            </div>
                          </td>
                          <td style={{ minWidth: 160 }}>
                            <div className="d-flex align-items-center gap-2">
                              <span className={`badge ${ (isEditing ? edit.isActive : r.isActive) ? 'bg-success' : 'bg-secondary'}`}>
                                {(isEditing ? edit.isActive : r.isActive) ? 'Активен' : 'Отключен'}
                              </span>
                              <Form.Check
                                type="switch"
                                id={`active-${r._id}`}
                                checked={isEditing ? !!edit.isActive : !!r.isActive}
                                onChange={(e) => {
                                  if (isEditing) {
                                    onEditChange(r._id, 'isActive', e.target.checked);
                                  } else {
                                    quickToggleActive(r._id, e.target.checked);
                                  }
                                }}
                              />
                            </div>
                          </td>
                          <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
                          <td style={{ minWidth: 210 }}>
                            {isEditing ? (
                              <div className="d-flex gap-2 flex-wrap">
                                <Button size="sm" variant="success" onClick={() => saveEdit(r._id)}>Сохранить</Button>
                                <Button size="sm" variant="secondary" onClick={() => cancelEdit(r._id)}>Отменить</Button>
                              </div>
                            ) : (
                              <div className="d-flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline-primary" onClick={() => beginEdit(r._id)}>Редактировать</Button>
                                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteClick(r)}>🗑️ Удалить</Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <div className="mt-2">
                <Button size="sm" variant="secondary" onClick={loadRestaurants}>Обновить</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal для подтверждения удаления ресторана */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {restaurantToDelete && (
            <div>
              <p>
                <strong>Вы уверены, что хотите удалить ресторан?</strong>
              </p>
              <div className="bg-light p-3 rounded">
                <p className="mb-1"><strong>Название:</strong> {restaurantToDelete.restaurantName}</p>
                <p className="mb-1"><strong>Email:</strong> {restaurantToDelete.email}</p>
                <p className="mb-1"><strong>Телефон:</strong> {restaurantToDelete.phone || '—'}</p>
                <p className="mb-1"><strong>Адрес:</strong> {restaurantToDelete.address || '—'}</p>
                <p className="mb-0"><strong>Статус:</strong> {restaurantToDelete.isActive ? 'Активен' : 'Отключен'}</p>
              </div>
              <div className="alert alert-danger mt-3 mb-0">
                <strong>⚠️ Внимание!</strong> Это действие нельзя отменить. Будут удалены все данные ресторана, включая меню, заказы и менеджеров.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={deleting}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Удаление...' : '🗑️ Удалить ресторан'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal для управления субдоменом */}
      <Modal show={subdomainModal.show} onHide={handleSubdomainModalClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Управление субдоменом</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {subdomainModal.restaurant && (
            <div>
              <div className="mb-3">
                <strong>Ресторан:</strong> {subdomainModal.restaurant.restaurantName}
              </div>
              
              {subdomainMessage && <Alert variant="success">{subdomainMessage}</Alert>}
              {subdomainError && <Alert variant="danger">{subdomainError}</Alert>}
              
              <Form.Group className="mb-3">
                <Form.Label>Субдомен</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    placeholder="restaurant-name"
                    maxLength={30}
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleGenerateSubdomain}
                    disabled={subdomainSaving}
                    title="Автоматически сгенерировать субдомен на основе названия ресторана"
                  >
                    ✨
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Только буквы, цифры и дефисы. Длина от 3 до 30 символов.
                  <br />
                  <strong>Совет:</strong> Нажмите ✨ для автоматической генерации на основе названия ресторана.
                </Form.Text>
              </Form.Group>

              {subdomain && (
                <div className="mb-3">
                  <strong>Ссылка ресторана:</strong>
                  <div className="mt-2">
                    <code>https://{subdomain}.menu.qr-code.by/</code>
                  </div>
                </div>
              )}

            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {subdomainModal.restaurant?.subdomain && (
                <Button 
                  variant="danger" 
                  onClick={handleSubdomainDelete}
                  disabled={subdomainSaving}
                  className="me-2"
                >
                  {subdomainSaving ? 'Удаление...' : '🗑️ Удалить субдомен'}
                </Button>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={handleSubdomainModalClose} disabled={subdomainSaving} className="me-2">
                Отмена
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSubdomainSave}
                disabled={subdomainSaving || !subdomain.trim()}
              >
                {subdomainSaving ? 'Сохранение...' : 'Сохранить субдомен'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SuperAdminDashboard;